"use client"

import Link from "next/link"
import { usePathname } from "next/navigation"

import { ColdApproachWordmark } from "@/components/marketing/cold-approach-wordmark"

// /what-happens-next is FUNNEL CHROME at both ends: a booked prospect gets no
// navigation to wander back into comparing programs and is never re-asked for
// his email. The two funnel pieces here are the COMPONENTS of real module
// instances (mastheads.funnel and footers.funnel), so the module sheet shows
// exactly what this page renders - the module is the section, never a
// side-door copy of it.
//
// /apply joins it, one step earlier. The checkout at /pay already strips its
// chrome so a buyer cannot wander back into browsing; the application that
// FEEDS that checkout must not carry the whole marketing site - the nav, every
// program link, the footer, and a newsletter email box that would be a second
// email ask competing with the one that matters.
//
// /book is the third, in the same lane: the calendar a man reaches from the
// application. A man picking a time has one thing to do, and a nav full of
// program links is an invitation to stop doing it.
export const FUNNEL_CHROME_ROUTES = new Set(["/what-happens-next", "/apply", "/book"])

// The /pay routes get CHECKOUT chrome (owner, 2026-08-20): a buyer at a
// $3,000 checkout gets no navigation to wander back into product browsing —
// just the lockup, a secure-checkout marker, and a one-line legal footer.
// Prefix-matched because /pay/embedded carries query strings and /pay/return
// carries Stripe's redirect params.
export const CHECKOUT_CHROME_PREFIX = "/pay/"

// The community membership checkout is the same kind of surface and gets the
// same chrome (owner: "the checkout for the community is totally wrong. We
// should have a unified checkout... apply those edits to this checkout as
// well."). It cannot ride the prefix above because it lives under /community,
// so it is an exact route: /community itself is the page meant to rank and
// keeps the full site chrome.
export const CHECKOUT_CHROME_ROUTES = new Set(["/community/join"])

/** Logo-only funnel header: the lockup alone, no categories and no Apply.
 *  The page's own Add to calendar is the ask. */
export function FunnelMasthead() {
  return (
    <header className="sticky top-0 z-40 border-b border-[var(--color-rule)] bg-white">
      <div className="mx-auto flex h-16 max-w-[92rem] items-center px-5 py-3 sm:px-8 lg:px-10">
        <Link
          href="/"
          aria-label="Cold Approach home"
          className="flex h-10 shrink-0 items-center text-[var(--color-ink)] transition-opacity hover:opacity-80"
        >
          <ColdApproachWordmark size={90} decorative />
        </Link>
      </div>
    </header>
  )
}

/** Renders the funnel node on funnel routes, the checkout node on /pay
 *  routes, and the site chrome everywhere else. Every side arrives as a
 *  ModuleSlot mount from the layout, so the usage analyzer sees every
 *  instance in the strict spelling. */
export function ChromeSwitch({
  funnel,
  checkout,
  children,
}: {
  funnel: React.ReactNode
  checkout: React.ReactNode
  children: React.ReactNode
}) {
  const pathname = usePathname()
  if (pathname && FUNNEL_CHROME_ROUTES.has(pathname)) return <>{funnel}</>
  if (pathname?.startsWith(CHECKOUT_CHROME_PREFIX)) return <>{checkout}</>
  if (pathname && CHECKOUT_CHROME_ROUTES.has(pathname)) return <>{checkout}</>
  return <>{children}</>
}
